import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import { getRegistrationDetails, resendOtp, verifyOTP } from '../api/registration';
import Cookies from 'js-cookie';
import { resolvePageContext, resolveRegisterPath } from '../utils/fanMemberUtils';
import { getFanMemberBrandTheme } from '../config/fanMembersConfig';

const OTPPage = ({ fixedActivityType, fixedId, fixedFanSlug }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();
  const params = useParams();
  const pageContext = resolvePageContext({ fixedFanSlug, fixedActivityType, fixedId, params });
  const { activityId: id, activityType, isFanMemberRoute, navigateTo, fanMember } = pageContext;
  const brand = getFanMemberBrandTheme(fanMember);
  const activityId = id;
  const registerPath = resolveRegisterPath({ isFanMemberRoute, navigateTo, activityType, activityId });
  const [registrationId, setRegistrationId] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    const regsitrationIdFromCookie = Cookies.get('registrationId');
    const phoneNumberFromCookie = Cookies.get('phoneNumber');
    if (!regsitrationIdFromCookie || !phoneNumberFromCookie) {
      navigate(registerPath);
      return;
    }
    setRegistrationId(regsitrationIdFromCookie);
    setPhoneNumber(phoneNumberFromCookie);
  }, [registerPath, navigate]);

  useEffect(() => {
    if (registrationId) {
      getRegistrationDetails(registrationId, activityId);
    }
  }, [registrationId, activityId]);

  const handleChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.querySelector(`input[name=otp-${index + 1}]`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[name=otp-${index - 1}]`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setMessage({ text: 'Please enter a valid 6-digit verification code', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await verifyOTP(registrationId, otpValue);
      if (response.success) {
        Cookies.set('registrationId', response.data.id);
        Cookies.set('phoneNumber', response.data.user.phoneNumber);

        if (activityType === 'fan') {
          if (isFanMemberRoute) {
            navigate(navigateTo('success'));
            setTimeout(() => {
              navigate(navigateTo('member'));
            }, 2000);
          } else {
            navigate(`/${activityType}/${activityId}/success`);
            setTimeout(() => {
              navigate(`/${activityType}/${activityId}/member`);
            }, 2000);
          }
        } else if (activityType === 'event') {
          if (isFanMemberRoute) {
            navigate(navigateTo('qr-code'));
          } else {
            navigate(`/${activityType}/${activityId}/qr-code`);
          }
        } else {
          if (isFanMemberRoute) {
            navigate(navigateTo('success'));
          } else {
            navigate(`/${activityType}/${activityId}/success`);
          }
        }
      }
    } catch (err) {
      setMessage({ text: err.response?.data?.error || err.message || 'Failed to verify OTP', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onClickResendOTP = async () => {
    setMessage({ text: '', type: '' });
    setOtp(['', '', '', '', '', '']);
    try {
      const response = await resendOtp(registrationId);
      console.log(response.plainOtp);
      if (response.success) {
        setMessage({ text: 'Verification code has been resent successfully', type: 'success' });
      }
    } catch (err) {
      setMessage({ text: err.message || 'Failed to resend verification code', type: 'error' });
    }
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${isFanMemberRoute ? brand.pageBg : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
      {!phoneNumber ? (
        <div className="flex justify-center items-center">
          <BeatLoader
            color={isFanMemberRoute ? brand.loader : '#7c3aed'}
            size={20}
          />
        </div>
      ) : (
        <div className={`w-full max-w-md bg-white rounded-lg shadow-lg p-6 sm:p-8 border ${isFanMemberRoute ? brand.cardBorder : 'border-blue-200'}`}>
          {/* Security Icon */}
          <div className="text-center mb-6">
            <div className={`mx-auto w-16 h-16 ${isFanMemberRoute ? brand.iconBg : 'bg-gradient-to-br from-blue-500 to-purple-600'} rounded-full flex items-center justify-center mb-4`}>
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Verification Required
            </h2>
            <p className="text-gray-600">
              We've sent a 6-digit verification code to
            </p>
            <p className={`font-medium mt-1 ${isFanMemberRoute ? brand.accentText : 'text-blue-600'}`}>
              {activityType === 'fan' ? "Your Email" : phoneNumber}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  name={`otp-${index}`}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  maxLength={1}
                  pattern="[0-9]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  disabled={loading}
                  className={`w-12 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg focus:outline-none transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${isFanMemberRoute ? brand.focusBorder : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'}`}
                />
              ))}
            </div>

            {message.text && (
              <div className={`text-center p-4 rounded-lg ${message.type === 'error'
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
                }`}>
                {message.text}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || otp.join('').length !== 6}
              className={`w-full py-3 px-4 text-white font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isFanMemberRoute ? brand.button + ' ' + brand.focusRing : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:ring-blue-500'}`}
            >
              {loading ? (
                <BeatLoader color="#ffffff" size={20} />
              ) : (
                'Verify Code'
              )}
            </button>

            <div className="text-center">
              <span className="text-gray-500">Didn't receive the code? </span>
              <button
                type="button"
                onClick={onClickResendOTP}
                className={`font-medium transition-colors ${isFanMemberRoute ? brand.link : 'text-blue-600 hover:text-blue-700'}`}
              >
                Resend Code
              </button>
            </div>
          </form>

          {/* Additional Security Notice */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              This code will expire in 10 minutes for security purposes
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OTPPage;